import { z } from 'zod';
import { wholeEuroCap } from './shared/euroCap.schema';

// Messages are translation keys (cockpit namespace); the form resolves them with t().
const euroCap = wholeEuroCap({
  minKey: 'smart_invites_limitations_validation_min_zero',
  centsKey: 'smart_invites_limitations_validation_cents',
});

const countCap = (min: 0 | 1) =>
  z
    .number()
    .int('smart_invites_limitations_validation_whole_number')
    .min(
      min,
      min === 0
        ? 'smart_invites_limitations_validation_min_zero'
        : 'smart_invites_limitations_validation_min_one'
    )
    .nullable();

const percent = (min: 0 | 1, message: string) =>
  z.number(message).int(message).min(min, message).max(100, message);

/**
 * The Smart Invites Limitations editor. Money is in euros here (the API takes cents) and every
 * percentage is a whole percent, as the API stores it. A null cap means "no cap on this axis".
 */
export const SmartInviteLimitationsFormSchema = z
  .object({
    valueCapPerExperienceEuros: euroCap,
    valueCapPerYearEuros: euroCap,
    maxEventsPerPersonPerYear: countCap(0),
    maxDistinctGuestsPerCompanyPerYear: countCap(0),
    maxTicketsPerInvite: countCap(1),
    approachingThresholdPct: percent(
      1,
      'smart_invites_limitations_validation_threshold'
    ),
    hostsCanApprove: z.boolean(),
    // Nullable because the field is hidden while delegation is off: an emptied, hidden margin
    // must not block the save. It is required only while hosts can approve.
    hostClearanceMarginPct: percent(
      0,
      'smart_invites_limitations_validation_margin'
    ).nullable(),
    askGuestsForDeclaration: z.boolean(),
  })
  .superRefine((values, ctx) => {
    if (values.hostsCanApprove && values.hostClearanceMarginPct === null) {
      ctx.addIssue({
        code: 'custom',
        path: ['hostClearanceMarginPct'],
        message: 'smart_invites_limitations_validation_margin',
      });
    }
  });

export type SmartInviteLimitationsFormValues = z.infer<
  typeof SmartInviteLimitationsFormSchema
>;
